import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  ArcElement, Title, Tooltip, Legend, Filler
);

const API_URL = process.env.REACT_APP_API_URL || '';

function Analytics() {
  const theme = useTheme();
  const cardStyle = {
    background: theme.surface,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    padding: '1rem',
  };
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [allGigs, setAllGigs] = useState([]);
  const [selectedGig, setSelectedGig] = useState('all');
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    fetchActiveGig();
    fetchAllGigs();
    axios.get(`${API_URL}/api/suggestions`).then(res => setSuggestions(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [period, selectedGig]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const url = selectedGig === 'all'
        ? `${API_URL}/api/analytics/global?period=${period}`
        : `${API_URL}/api/analytics/gig/${selectedGig}?period=${period}`;
      const response = await axios.get(url);
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveGig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gigs/active`);
      setSelectedGig(response.data.unique_url);
    } catch {}
  };

  const fetchAllGigs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gigs`);
      setAllGigs(response.data);
    } catch {}
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading analytics...</div>;
  if (!analytics) return <div style={{ padding: '2rem' }}>No data available</div>;

  const stats = analytics.stats;

  const topSongsData = {
    labels: analytics.topSongs.map(s => s.title),
    datasets: [{ label: 'Requests', data: analytics.topSongs.map(s => parseInt(s.request_count)), backgroundColor: '#667eea' }]
  };

  const topGenresData = {
    labels: analytics.topGenres.map(g => g.genre),
    datasets: [{
      data: analytics.topGenres.map(g => parseInt(g.request_count)),
      backgroundColor: ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b']
    }]
  };

  const requestsOverTimeData = {
    labels: analytics.requestsOverTime.map(r =>
      new Date(r.date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })
    ),
    datasets: [{
      label: 'Requests',
      data: analytics.requestsOverTime.map(r => parseInt(r.count)),
      borderColor: '#667eea',
      backgroundColor: 'rgba(102, 126, 234, 0.1)',
      tension: 0.4,
      fill: true
    }]
  };

  const topVenuesData = analytics.topVenues ? {
    labels: analytics.topVenues.map(v => v.venue_name),
    datasets: [{ label: 'Requests', data: analytics.topVenues.map(v => parseInt(v.request_count)), backgroundColor: '#764ba2' }]
  } : null;

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, padding: 10, font: { size: 12 } } } }
  };

  return (
    <div style={{ background: theme.background }}>
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '1rem', color: theme.text }}>

      {/* Controls */}
      <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <select
          value={selectedGig}
          onChange={(e) => setSelectedGig(e.target.value)}
          style={{
            padding: '0.6rem 0.75rem', border: `1px solid ${theme.border}`, borderRadius: '8px',
            fontSize: '0.9rem', color: theme.text, background: theme.surface, cursor: 'pointer',
            fontWeight: '500', width: '100%',
          }}
        >
          <option value="all">All Gigs</option>
          {allGigs.map(gig => (
            <option key={gig.id} value={gig.unique_url}>
              {gig.gig_name} @ {gig.venue_name}
            </option>
          ))}
        </select>

        <div style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', paddingBottom: '2px' }}>
          {['week', 'month', 'year', 'all'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: '0.4rem 0.85rem', flexShrink: 0,
                background: period === p ? theme.primary : theme.surface,
                color: period === p ? theme.onPrimary : theme.textSecondary,
                border: `1px solid ${period === p ? theme.primary : theme.border}`,
                borderRadius: '8px', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: period === p ? '600' : '400',
              }}
            >
              {p === 'all' ? 'All time' : `Past ${p}`}
            </button>
          ))}
        </div>
      </div>

      {/* Stats — 2×2 grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '0.6rem',
        marginBottom: '1rem',
      }}>
        {[
          { label: 'Total Requests', value: stats.total_requests || 0 },
          { label: 'Unique Songs',   value: stats.unique_songs || 0 },
          { label: 'Unique Requesters', value: stats.unique_requesters || 0 },
          { label: 'Completed',      value: stats.completed_requests || 0 },
        ].map(({ label, value }) => (
          <div key={label} style={{ ...cardStyle, background: theme.surfaceAlt }}>
            <p style={{ fontSize: '11px', color: theme.textMuted, margin: '0 0 0.3rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {label}
            </p>
            <p style={{ fontSize: '1.75rem', fontWeight: '700', margin: 0, color: theme.text, lineHeight: 1 }}>
              {value}
            </p>
          </div>
        ))}

        {selectedGig === 'all' && (
          <div style={{ ...cardStyle, background: theme.surfaceAlt, gridColumn: '1 / -1' }}>
            <p style={{ fontSize: '11px', color: theme.textMuted, margin: '0 0 0.3rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Total Gigs
            </p>
            <p style={{ fontSize: '1.75rem', fontWeight: '700', margin: 0, color: theme.text, lineHeight: 1 }}>
              {stats.total_gigs || 0}
            </p>
          </div>
        )}
      </div>

      {/* Top Songs Bar Chart */}
      <div style={{ ...cardStyle, marginBottom: '0.75rem' }}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: '700' }}>Top Requested Songs</h3>
        {analytics.topSongs.length > 0 ? (
          <div style={{ height: '220px' }}>
            <Bar data={topSongsData} options={barOptions} />
          </div>
        ) : (
          <p style={{ color: theme.textSecondary, textAlign: 'center', padding: '1.5rem', margin: 0 }}>No requests yet</p>
        )}
      </div>

      {/* Top Genres Doughnut */}
      <div style={{ ...cardStyle, marginBottom: '0.75rem' }}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: '700' }}>Top Genres</h3>
        {analytics.topGenres.length > 0 ? (
          <div style={{ height: '240px' }}>
            <Doughnut data={topGenresData} options={doughnutOptions} />
          </div>
        ) : (
          <p style={{ color: theme.textSecondary, textAlign: 'center', padding: '1.5rem', margin: 0 }}>No requests yet</p>
        )}
      </div>

      {/* Requests Over Time */}
      {analytics.requestsOverTime.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: '0.75rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: '700' }}>Requests Over Time</h3>
          <div style={{ height: '200px' }}>
            <Line data={requestsOverTimeData} options={barOptions} />
          </div>
        </div>
      )}

      {/* Top Venues — global only */}
      {selectedGig === 'all' && analytics.topVenues && analytics.topVenues.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: '0.75rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: '700' }}>Top Venues by Requests</h3>
          <div style={{ height: '220px' }}>
            <Bar data={topVenuesData} options={barOptions} />
          </div>
        </div>
      )}

      {/* Detailed Rankings Table */}
      <div style={{ ...cardStyle, marginBottom: '0.75rem' }}>
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: '700' }}>Detailed Rankings</h3>
        {analytics.topSongs.length > 0 ? (
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.4rem', color: theme.textMuted, fontWeight: '600', width: '24px' }}>#</th>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.4rem', color: theme.textMuted, fontWeight: '600' }}>Song</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0.4rem', color: theme.textMuted, fontWeight: '600', whiteSpace: 'nowrap' }}>Reqs</th>
              </tr>
            </thead>
            <tbody>
              {analytics.topSongs.map((song, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '0.55rem 0.4rem', color: theme.textMuted, fontWeight: '600' }}>{idx + 1}</td>
                  <td style={{ padding: '0.55rem 0.4rem' }}>
                    <div style={{ fontWeight: '500', color: theme.text }}>{song.title}</div>
                    <div style={{ fontSize: '11px', color: theme.textMuted, marginTop: '1px' }}>
                      {song.artist_original}
                      {song.genre ? <span style={{ marginLeft: '0.4rem', background: theme.surfaceAlt, padding: '1px 6px', borderRadius: '8px' }}>{song.genre}</span> : null}
                    </div>
                  </td>
                  <td style={{ padding: '0.55rem 0.4rem', textAlign: 'right', fontWeight: '700', color: theme.primary }}>
                    {song.request_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: theme.textSecondary, textAlign: 'center', padding: '1.5rem', margin: 0 }}>No data available for this period</p>
        )}
      </div>

      {/* Song Suggestions */}
      <div style={{ ...cardStyle }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <h3 style={{ margin: '0 0 0.2rem', fontSize: '0.95rem', fontWeight: '700' }}>Song Suggestions</h3>
          <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted }}>
            Crowd-sourced ideas from your audience — all gigs, all time
          </p>
        </div>
        {suggestions.length > 0 ? (
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.4rem', color: theme.textMuted, fontWeight: '600', width: '24px' }}>#</th>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.4rem', color: theme.textMuted, fontWeight: '600' }}>Song</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0.4rem', color: theme.textMuted, fontWeight: '600' }}>Count</th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map((s, idx) => (
                <tr key={idx} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '0.55rem 0.4rem', color: theme.textMuted, fontWeight: '600' }}>{idx + 1}</td>
                  <td style={{ padding: '0.55rem 0.4rem' }}>
                    <div style={{ fontWeight: '500', color: theme.text }}>{s.title}</div>
                    {s.artist_name && <div style={{ fontSize: '11px', color: theme.textMuted, marginTop: '1px' }}>{s.artist_name}</div>}
                  </td>
                  <td style={{ padding: '0.55rem 0.4rem', textAlign: 'right' }}>
                    <span style={{
                      background: parseInt(s.suggestion_count) >= 3 ? theme.primary : theme.surfaceAlt,
                      color: parseInt(s.suggestion_count) >= 3 ? theme.onPrimary : theme.textSecondary,
                      padding: '0.2rem 0.6rem', borderRadius: '10px',
                      fontSize: '12px', fontWeight: '700',
                    }}>
                      {s.suggestion_count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem', margin: 0, fontSize: '13px' }}>
            No suggestions yet. The suggestion box appears at the bottom of your audience gig page.
          </p>
        )}
      </div>
    </div>
    </div>
  );
}

export default Analytics;
